import { FormEvent, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useResetPassword } from '../hooks/useAuth'
import { ApiError } from '../lib/api'
import { Wordmark } from '../components/ui/Wordmark'

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [passwordError, setPasswordError] = useState<string | undefined>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const resetPassword = useResetPassword()

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setPasswordError(undefined)

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setPasswordError('Passwords do not match')
      return
    }
    if (!token) {
      setPasswordError('Invalid reset link')
      return
    }

    resetPassword.mutate({ token, password })
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
        <div className="mb-7"><Wordmark size={22} /></div>
        <div className="bg-surface border border-line-soft rounded-[20px] px-9 py-8 w-full max-w-[400px] text-center">
          <p className="m-0 mb-4 text-[13px] text-[#A33A2A]">Invalid or expired reset link.</p>
          <Link to="/forgot-password" className="text-primary text-[13px] font-semibold no-underline">
            Request a new link
          </Link>
        </div>
      </div>
    )
  }

  const serverError = resetPassword.error instanceof ApiError ? resetPassword.error.message : null

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 py-10">
      <div className="mb-7"><Wordmark size={22} /></div>

      <div className="bg-surface border border-line-soft rounded-[20px] px-9 py-8 w-full max-w-[400px]">
        <h2 className="serif text-3xl tracking-tight m-0 mb-6">Set new password</h2>

        {serverError && (
          <div role="alert" className="px-3.5 py-2.5 rounded-[10px] mb-4 bg-[#FAEBE7] border border-[#E9C8C0] text-[#7A2A1E] text-[13px]">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4 flex flex-col gap-1.5">
            <label htmlFor="password" className="text-xs font-semibold text-ink-2">
              New password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input h-[46px]"
            />
          </div>

          <div className="mb-6 flex flex-col gap-1.5">
            <label htmlFor="confirm" className="text-xs font-semibold text-ink-2">
              Confirm password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input h-[46px]"
              aria-describedby={passwordError ? 'password-error' : undefined}
              aria-invalid={Boolean(passwordError)}
            />
            {passwordError && (
              <div id="password-error" className="text-[11px] text-[#A33A2A]">{passwordError}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={resetPassword.isPending}
            className="btn btn--full btn--lg"
          >
            {resetPassword.isPending ? 'Saving…' : 'Set password'}
          </button>
        </form>
      </div>
    </div>
  )
}
